import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Text } from './base-components';
import { useWaitlistEntries } from '../hooks/useApi';
import { WaitlistEntry, WaitlistEventGroup } from '../types';
import {
  formatEventStart,
  formatJoinedAt,
  formatSyncedAgo,
  waitlistEmptyReason,
} from './waitlistStatus';
import { WaitlistClaimCode } from './WaitlistClaimCode';

const statusPill = (status: string) => {
  switch (status) {
    case 'joined':
      return 'bg-green-500/10 text-green-400 border-green-500/20';
    case 'purchased':
      return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    case 'timed-out':
      return 'bg-white/5 text-white/40 border-white/10';
    default:
      return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
  }
};

// Dice's status set is open, so an unrecognised value must still read as
// "on this waitlist" rather than disappearing behind a default.
const statusLabel = (status: string) => {
  switch (status) {
    case 'joined':
      return 'On waitlist';
    case 'purchased':
      return 'Got tickets';
    case 'timed-out':
      return 'Offer expired';
    default:
      return 'On waitlist';
  }
};

const EntryRow: React.FC<{ entry: WaitlistEntry }> = ({ entry }) => {
  const joined = formatJoinedAt(entry.joined_at);

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <span className="text-sm text-white/80 flex-shrink-0">{entry.account_label}</span>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs text-white/50 truncate">
          {entry.ticket_type_name || 'Ticket type unknown'}
          {entry.number_of_tickets ? ` · ${entry.number_of_tickets} ticket${entry.number_of_tickets === 1 ? '' : 's'}` : ''}
        </span>
        {/* Absent for the ~95% of entries joined outside FlashTools. */}
        {joined && <span className="text-[11px] text-white/30">{joined}</span>}
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${statusPill(entry.status)}`}>
        {statusLabel(entry.status)}
      </span>
    </div>
  );
};

const EventGroup: React.FC<{
  group: WaitlistEventGroup;
  accountsPath: string;
  billingPath: string;
}> = ({ group, accountsPath, billingPath }) => (
  <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
    <div className="flex gap-3">
      {group.event.images?.square && (
        <img
          src={group.event.images.square}
          alt={group.event.name}
          className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
        />
      )}
      <div className="flex flex-col min-w-0 flex-1">
        {group.event.dice_url ? (
          <a
            href={group.event.dice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-white hover:text-blue-400 transition-colors truncate"
          >
            {group.event.name}
          </a>
        ) : (
          <span className="text-sm font-medium text-white truncate">{group.event.name}</span>
        )}
        <span className="text-xs text-white/40 truncate">
          {formatEventStart(group.event.event_start_utc, group.event.timezone)}
          {group.event.venue && ` · ${group.event.venue}`}
        </span>
        <span className="text-[11px] text-white/30 mt-0.5">
          {group.total_entries} account{group.total_entries === 1 ? '' : 's'} · {group.total_tickets} ticket
          {group.total_tickets === 1 ? '' : 's'}
        </span>
      </div>
    </div>

    <div className="mt-3 space-y-1.5">
      {group.entries.map(entry => (
        <EntryRow key={`${entry.account_id}-${entry.ticket_type_id ?? 'na'}`} entry={entry} />
      ))}
    </div>

    {/* A code only ever reaches an account already on this waitlist, which is
        exactly the list above — so the claim sits here rather than on the
        sold-out browse cards, where the event is one nobody has joined yet. */}
    <WaitlistClaimCode
      eventId={group.event.event_id}
      accounts={claimAccounts(group)}
      accountsPath={accountsPath}
      billingPath={billingPath}
    />
  </div>
);

/**
 * One option per account on this waitlist, deduped: an account with entries on
 * two ticket types is still one account to claim with.
 */
const claimAccounts = (group: WaitlistEventGroup) => {
  const seen = new Map<number, string>();
  group.entries.forEach(entry => {
    if (!seen.has(entry.account_id)) seen.set(entry.account_id, entry.account_label);
  });
  return Array.from(seen, ([id, label]) => ({ id, label }));
};

interface Props {
  /** Where "sync"/"connect" links point. The v2 tree namespaces its routes. */
  accountsPath?: string;
  /** Where an upgrade prompt points, for the same reason. */
  billingPath?: string;
}

export const MyWaitlistsSection: React.FC<Props> = ({
  accountsPath = '/accounts',
  billingPath = '/billing',
}) => {
  const { data, isLoading, error } = useWaitlistEntries();
  // Collapsed by default: this is reference material, not the page's job. The
  // header still carries the counts, so collapsing hides detail, not signal.
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return <div className="h-11 rounded-xl bg-white/[0.03] animate-pulse" />;
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/[0.06]">
        <Text size="sm" color="muted">{(error as Error).message}</Text>
      </div>
    );
  }

  if (!data) return null;

  const { sync, data: groups, truncated } = data;
  const staleCount = sync.stale_account_count;
  const totalEntries = data.total_entries;

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <button
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <svg
          className={`w-3.5 h-3.5 text-white/40 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-sm font-medium text-white">My waitlists</span>
        <span className="text-xs text-white/40">
          {groups.length === 0
            ? 'none'
            : `${totalEntries} across ${groups.length} event${groups.length === 1 ? '' : 's'}`}
        </span>
        {staleCount > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
            {staleCount} out of date
          </span>
        )}
        <span className="ml-auto text-xs text-white/30">{formatSyncedAgo(sync.last_complete_at)}</span>
      </button>

      {expanded && (
    <div className="space-y-3 px-4 pb-4">
      {/* stale_account_ids are accounts whose sync failed or never ran — their
          waitlists are missing or outdated regardless of what's listed below. */}
      {staleCount > 0 && (
        <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/10">
          <p className="text-sm text-amber-300">
            {staleCount} account{staleCount === 1 ? '' : 's'} out of date — this list may be
            incomplete.{' '}
            <Link to={accountsPath} className="underline underline-offset-2 hover:text-amber-200">
              Sync or reconnect
            </Link>
          </p>
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyWaitlists reason={waitlistEmptyReason(sync)} accountsPath={accountsPath} />
      ) : (
        <div className="space-y-3">
          {groups.map(group => (
            <EventGroup
              key={group.event.event_id}
              group={group}
              accountsPath={accountsPath}
              billingPath={billingPath}
            />
          ))}
        </div>
      )}

      {truncated && (
        <Text size="xs" color="muted">
          Showing the first 2000 entries. Narrow the filters to see the rest.
        </Text>
      )}
    </div>
      )}
    </div>
  );
};

// The three empty states are distinct on purpose — "never synced" must never
// be rendered as "you're on none".
const EmptyWaitlists: React.FC<{
  reason: ReturnType<typeof waitlistEmptyReason>;
  accountsPath: string;
}> = ({ reason, accountsPath }) => {
  if (reason === 'no_accounts') {
    return (
      <div className="text-center py-8 rounded-xl border border-white/[0.06] bg-white/[0.02]">
        <Text size="sm" color="muted">Connect a Dice account to see your waitlists</Text>
        <Link
          to={accountsPath}
          className="inline-block mt-2 text-sm text-[#3b82f6] hover:underline"
        >
          Connect an account
        </Link>
      </div>
    );
  }

  if (reason === 'never_synced') {
    return (
      <div className="text-center py-8 rounded-xl border border-white/[0.06] bg-white/[0.02]">
        <Text size="sm" color="muted">Sync your accounts to see your waitlists</Text>
        <Link
          to={accountsPath}
          className="inline-block mt-2 text-sm text-[#3b82f6] hover:underline"
        >
          Go to accounts to sync
        </Link>
      </div>
    );
  }

  return (
    <div className="text-center py-8 rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <Text size="sm" color="muted">You're not on any waitlists</Text>
    </div>
  );
};
